/**
 * payloadTransformer — the single, configuration-driven payload engine shared by
 * AddFormV1 and EditFormV1.
 *
 * Goal: the Admin form-group config is the ONLY source of truth for how a form
 * value becomes an API payload value. There is ZERO field-name / module-specific
 * branching here. Every behaviour is driven by these per-field config keys:
 *
 *   payloadKey      output key (dot-notation allowed). Default: field.field
 *   dataType        auto | string | number | boolean | array | object | date | custom
 *   elementType     for dataType "array": coerce each element (e.g. "number")
 *   payloadMode     auto | value | label | object | custom | template | skip
 *   valueKey        which key holds the id/value on an option object (default: value/id/_id…)
 *   displayKey      which key holds the label on an option object (default: label/name…)
 *   payloadTemplate object template for custom/template modes, with {{value}} {{label}} {{raw}} tokens
 *   defaultValue    value substituted when the form value is empty
 *   transformRule   { map: {...}, default, name } — value maps / named transforms
 *   omitEmpty       drop the key entirely when the final value is empty
 *
 * Nothing here knows about "priority", "recruiters", "noticePeriod", etc. Those
 * are expressed purely through the config above.
 *
 * Backward compatibility: when a field carries NONE of the new keys, the engine
 * falls back to the historical generic behaviour — dot-notation nesting, dayjs →
 * ISO string, scalar pass-through — so existing forms keep working unchanged.
 */

import dayjs from 'dayjs';
import { fromAppInput, shouldConvertToZone } from '../../services/timezone';
import { securePayload } from './inputSecurity';
import { overrideConfig, overrideFieldKey, overrideIsStored } from './validationOverride';
import { snapToOption } from './optionMatching';

// ── small shared predicates ───────────────────────────────────────────────────

const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

// truthy tolerates the shapes a boolean config flag can arrive in (true/1/"1"),
// matching how the forms read the same flags elsewhere.
const truthy = (v) => v === true || v === 1 || v === '1';

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v) && !isDayjs(v);

function isDayjs(v) {
  return (
    v &&
    typeof v === 'object' &&
    typeof v.format === 'function' &&
    typeof v.isValid === 'function'
  );
}

function isDateLike(v) {
  return isDayjs(v) || v instanceof Date;
}

function toISO(v) {
  if (v instanceof Date) return v.toISOString();
  if (isDayjs(v)) return v.isValid() ? v.toISOString() : null;
  return v;
}

// toStoredDate — serialise a picker value, honouring the tenant timezone for
// fields that carry an actual INSTANT.
//
// A DatePicker/TimePicker hands back a value in the BROWSER's zone. On a tenant
// configured to Asia/Dubai, a user choosing 09:00 means 09:00 in Dubai; storing
// the browser's 09:00 would be a different moment entirely.
//
// The guard matters as much as the conversion: plain CALENDAR dates (date of
// birth, passport expiry, education start) mean the same day in every zone, and
// converting them shifts them by a day for half the world. shouldConvertToZone
// only opts in datetime/time fields, or fields explicitly marked tzAware — see
// services/timezone.js.
function toStoredDate(value, field) {
  if (!shouldConvertToZone(field)) return toISO(value);
  const zoned = fromAppInput(value);
  return zoned ? zoned.toISOString() : toISO(value);
}

// ── dot-notation get / set ────────────────────────────────────────────────────

export function getDeep(obj, path) {
  if (!path) return undefined;
  const parts = String(path).split('.');
  let cur = obj;
  for (const part of parts) {
    if (cur == null) return undefined;
    cur = cur[part];
  }
  return cur;
}

// readFormValue reads a field value tolerating BOTH antd conventions used in this
// codebase: a flat dotted key ("experience.from", as EditForm registers fields)
// and a nested object ("experience": { from }, as AddForm registers fields).
export function readFormValue(values, path) {
  if (values && Object.prototype.hasOwnProperty.call(values, path)) return values[path];
  return getDeep(values, path);
}

// hasFormValue reports whether a submit actually carried this field (so EditForm
// can leave untouched parts of the base record alone).
export function hasFormValue(values, path) {
  if (values && Object.prototype.hasOwnProperty.call(values, path)) return true;
  return getDeep(values, path) !== undefined;
}

export function setDeep(target, path, value) {
  const parts = String(path).split('.');
  let cur = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (!isPlainObject(cur[key])) cur[key] = {};
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
  return target;
}

/**
 * mergeDeepAt — setDeep, except that when BOTH the existing value at `path` and
 * the incoming one are plain objects the keys are merged, with the EXISTING
 * value winning every collision.
 *
 * A stored file container is written from two places: the file field carries
 * the whole container back (passport → {passportCopyLocation, passportUploadName,
 * passportNumber, …}) while its sibling fields write individual keys into the
 * same container (passport.passportNumber). Plain assignment would let whichever
 * ran last erase the other — including overwriting a number the user just edited
 * with the stale one from the carried snapshot. Existing-wins makes the result
 * independent of field order.
 */
function mergeDeepAt(target, path, value) {
  const parts = String(path).split('.');
  let cur = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (!isPlainObject(cur[key])) cur[key] = {};
    cur = cur[key];
  }
  const last = parts[parts.length - 1];
  cur[last] = (isPlainObject(cur[last]) && isPlainObject(value))
    ? { ...value, ...cur[last] }
    : value;
  return target;
}

/**
 * carriedFileValue — the stored reference a file field must write back, read
 * from its antd fileList.
 *
 * Only entries EXPLICITLY marked by the prefill/edit helpers count: those carry
 * the original container on `stored`. Any other object in a file value is
 * treated exactly as before — leftover/stale file metadata that must not be
 * written back (see the "strips stale file data" case in the payload tests).
 * A fresh upload (originFileObj) means the multipart path owns this field and
 * the downstream handler writes its shape, so nothing is carried — mixing the
 * two would put a stale reference over it.
 */
function carriedFileValue(value) {
  const list = Array.isArray(value) ? value : (value == null ? [] : [value]);
  if (!list.length) return undefined;
  if (list.some((item) => item?.originFileObj)) return undefined;
  const stored = list
    .map((item) => item?.stored)
    .filter((s) => s !== undefined && s !== null);
  if (!stored.length) return undefined;
  return stored.length === 1 && list.length === 1 ? stored[0] : stored;
}

/** Merge a plain object's keys into target at root (used by custom/template spread). */
function mergeDeep(target, source) {
  Object.entries(source ?? {}).forEach(([k, v]) => {
    if (isPlainObject(v) && isPlainObject(target[k])) mergeDeep(target[k], v);
    else target[k] = v;
  });
  return target;
}

// ── option / value-label normalisation ────────────────────────────────────────

function optionLabelFor(field, value) {
  const opts = field.options ?? field.values ?? [];
  for (const o of opts) {
    if (typeof o === 'string') {
      if (o === value) return o;
    } else if ((o.value ?? o.id ?? o.name ?? o.label) === value) {
      return o.label ?? o.name ?? o.value;
    }
  }
  return undefined;
}

/**
 * splitValueLabel — normalises a raw form value into { value, label }.
 * Handles antd labelInValue ({value,label}), {id,name}/{_id,…} option objects,
 * and plain primitives (label recovered from field.options when present).
 */
function splitValueLabel(raw, field) {
  if (isPlainObject(raw)) {
    const value =
      (field.valueKey && raw[field.valueKey]) ??
      raw.value ??
      raw.id ??
      raw._id ??
      raw.key ??
      raw.code;
    const label =
      (field.displayKey && raw[field.displayKey]) ??
      raw.label ??
      raw.name ??
      raw.text ??
      raw.title ??
      value;
    return { value, label };
  }
  return { value: raw, label: optionLabelFor(field, raw) ?? raw };
}

// effectiveDataType — the dataType to apply. An explicit dataType always wins;
// otherwise it is inferred from the field's input type / multiplicity so that a
// plain `type: "number"` or a multi-select keeps coercing without the admin
// having to set dataType on every field (keeps existing configs working).
export function effectiveDataType(field = {}) {
  if (field.dataType && field.dataType !== 'auto') return field.dataType;
  if (field.multiSelect || field.mode === 'multiple' || field.addRow) return 'array';
  switch (field.type) {
    case 'number':
      return 'number';
    case 'date':
    case 'time':
      return 'date';
    default:
      return 'auto';
  }
}

// ── data-type coercion ────────────────────────────────────────────────────────

export function coerceDataType(value, dataType, field = {}) {
  if (isDateLike(value)) {
    // Dates always serialise to ISO unless explicitly typed otherwise below.
    if (dataType === 'string') return toStoredDate(value, field);
    if (dataType === 'number') {
      const t = isDayjs(value) ? value.valueOf() : value.getTime();
      return Number.isNaN(t) ? null : t;
    }
    if (dataType === 'date' || dataType === 'auto' || !dataType) return toStoredDate(value, field);
  }

  switch (dataType) {
    case 'string':
      return value == null ? '' : String(value);

    case 'number': {
      if (isEmpty(value)) return null;
      const n = Number(value);
      return Number.isNaN(n) ? null : n;
    }

    case 'boolean':
      return value === true || value === 1 || value === '1' || value === 'true';

    case 'array': {
      let arr;
      if (Array.isArray(value)) arr = value;
      else if (isEmpty(value)) arr = [];
      else if (typeof value === 'string' && value.includes(','))
        arr = value.split(',').map((s) => s.trim()).filter(Boolean);
      else arr = [value];
      if (field.elementType) {
        return arr
          .map((el) => coerceDataType(el, field.elementType, {}))
          .filter((el) => el !== null && el !== undefined && el !== '');
      }
      return arr;
    }

    case 'object':
      return isPlainObject(value) ? value : value;

    case 'date':
      return toStoredDate(value, field);

    case 'custom':
    case 'auto':
    case undefined:
    case '':
    default:
      return value;
  }
}

// ── transformRule (value maps + named transforms) ─────────────────────────────

const NAMED_TRANSFORMS = {
  firstChecked: (v) => (Array.isArray(v) ? v[0] : v),
  csvToArray: (v) =>
    typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v,
  arrayToCsv: (v) => (Array.isArray(v) ? v.join(',') : v),
  trim: (v) => (typeof v === 'string' ? v.trim() : v),
  upper: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
  lower: (v) => (typeof v === 'string' ? v.toLowerCase() : v),
};

function applyTransformRule(value, transformRule) {
  if (!transformRule) return value;
  let rule = transformRule;
  if (typeof rule === 'string') {
    // Either a named transform or a JSON blob.
    if (NAMED_TRANSFORMS[rule]) return NAMED_TRANSFORMS[rule](value);
    try {
      rule = JSON.parse(rule);
    } catch {
      return value;
    }
  }

  let out = value;
  if (rule.name && NAMED_TRANSFORMS[rule.name]) out = NAMED_TRANSFORMS[rule.name](out);

  if (rule.map && typeof rule.map === 'object') {
    const key = Array.isArray(out) ? String(out[0]) : String(out);
    if (Object.prototype.hasOwnProperty.call(rule.map, key)) out = rule.map[key];
    else if (rule.default !== undefined) out = rule.default;
  }
  return out;
}

// ── template resolution (custom / template payload modes) ─────────────────────

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

function resolveToken(token, ctx) {
  const [head, ...rest] = token.split('.');
  let base;
  if (head === 'value') base = ctx.value;
  else if (head === 'label') base = ctx.label;
  else if (head === 'raw') base = ctx.raw;
  else return undefined;
  return rest.length ? getDeep(base, rest.join('.')) : base;
}

function resolveTemplateNode(node, ctx) {
  if (typeof node === 'string') {
    // Whole-string single token → return the typed value (keep numbers numeric).
    const whole = node.match(/^\{\{\s*([\w.]+)\s*\}\}$/);
    if (whole) return resolveToken(whole[1], ctx);
    return node.replace(TOKEN_RE, (_, tok) => {
      const v = resolveToken(tok, ctx);
      return v == null ? '' : String(v);
    });
  }
  if (Array.isArray(node)) return node.map((n) => resolveTemplateNode(n, ctx));
  if (isPlainObject(node)) {
    const out = {};
    Object.entries(node).forEach(([k, v]) => {
      out[k] = resolveTemplateNode(v, ctx);
    });
    return out;
  }
  return node;
}

function parseTemplate(template) {
  if (!template) return null;
  if (typeof template === 'string') {
    try {
      return JSON.parse(template);
    } catch {
      return null;
    }
  }
  return template;
}

// ── payloadMode shaping ───────────────────────────────────────────────────────

function shapeSingle(field, raw) {
  const mode = field.payloadMode || 'auto';
  const dataType = effectiveDataType(field);
  const { value, label } = splitValueLabel(raw, field);

  switch (mode) {
    case 'value':
      return { kind: 'scalar', out: coerceDataType(value, dataType, field) };

    case 'label':
      return { kind: 'scalar', out: coerceDataType(label, field.dataType || 'string', field) };

    case 'object': {
      const vKey = field.valueKey || 'id';
      const dKey = field.displayKey || 'name';
      return {
        kind: 'scalar',
        out: {
          [vKey]: coerceDataType(value, field.elementType || 'auto', {}),
          [dKey]: label,
        },
      };
    }

    case 'custom':
    case 'template': {
      const tpl = parseTemplate(field.payloadTemplate);
      if (!tpl) return { kind: 'scalar', out: coerceDataType(value, dataType, field) };
      const ctx = { value: coerceDataType(value, field.elementType || 'auto', {}), label, raw };
      const resolved = resolveTemplateNode(tpl, ctx);
      // A template that yields a plain object spreads into the parent unless the
      // admin pinned an explicit payloadKey.
      return { kind: field.payloadKey ? 'scalar' : 'spread', out: resolved };
    }

    case 'auto':
    default:
      return { kind: 'scalar', out: coerceDataType(value, dataType, field) };
  }
}

/**
 * shapeFieldValue — turns one field's raw form value into its payload contribution.
 * Returns { kind: 'scalar'|'spread'|'skip', out }.
 *   scalar → write `out` at the field's payloadKey
 *   spread → merge `out` (a plain object) into the parent payload
 *   skip   → contribute nothing
 */
export function shapeFieldValue(field, rawValue) {
  if (field.payloadMode === 'skip') return { kind: 'skip' };

  let raw = rawValue;
  if (isEmpty(raw) && field.defaultValue !== undefined && field.defaultValue !== '') {
    raw = field.defaultValue;
  }

  raw = applyTransformRule(raw, field.transformRule);

  if (isEmpty(raw) && field.omitEmpty) return { kind: 'skip' };

  // Multi-value (multi-select / array source) with a per-element shape.
  const isMultiSource =
    Array.isArray(raw) &&
    (effectiveDataType(field) === 'array' || field.payloadMode === 'object');

  if (isMultiSource && field.payloadMode && field.payloadMode !== 'value' && field.payloadMode !== 'auto') {
    const out = raw.map((item) => shapeSingle(field, item).out);
    return { kind: 'scalar', out };
  }

  return shapeSingle(field, raw);
}

export function usesLabeledSelectValue(field = {}) {
  return Boolean(field.richValue) || ['label', 'object', 'custom', 'template'].includes(field.payloadMode);
}

export function toSelectControlValue(field, value, options = []) {
  if (!usesLabeledSelectValue(field) || value == null) return value;
  const toLabeled = (item) => {
    if (isPlainObject(item) && item.value !== undefined) return item;
    const match = options.find((option) =>
      String(option?.value ?? '') === String(item) || String(option?.label ?? '') === String(item));
    return {
      value: match?.value ?? item,
      label: match?.label ?? String(item),
    };
  };
  return Array.isArray(value) ? value.map(toLabeled) : toLabeled(value);
}

// ── showIf payload gating ─────────────────────────────────────────────────────
//
// A field hidden by its showIf condition must not STORE either: antd preserves
// values of unmounted Form.Items, so "check VMS Required, type a commission,
// uncheck it" would still submit the commission. showIfSatisfied evaluates the
// same operators the forms render with, against the field's own scope (the row
// object for an addRow row) falling back to the whole form values (a condition
// field living in another group, e.g. contractType). writeField consults it and
// writes null instead of the stale value, so an edit also clears what a
// now-hidden field previously stored. Config-driven; no field names.
// One leaf condition ({field,operator,value}) evaluated against a scope.
function showIfLeafSatisfied(cond, scope, root) {
  if (!cond?.field) return true;
  const val = hasFormValue(scope, cond.field)
    ? readFormValue(scope, cond.field)
    : readFormValue(root ?? scope, cond.field);
  const list = () => String(cond.value ?? '').split(',').map((v) => v.trim());
  switch (cond.operator) {
    case 'eq': return String(val ?? '') === String(cond.value ?? '');
    case 'neq': return String(val ?? '') !== String(cond.value ?? '');
    case 'truthy': return val !== undefined && val !== null && val !== '' && val !== false;
    case 'falsy': return val === undefined || val === null || val === '' || val === false;
    case 'notEmpty': return Array.isArray(val) ? val.length > 0 : Boolean(val);
    case 'in': return list().includes(String(val ?? ''));
    case 'notIn': return !list().includes(String(val ?? ''));
    default: return true;
  }
}

// All field keys a Show Condition reads (single leaf + every conditions[] entry)
// — used to walk transitive visibility across each gate field.
export function showIfConditionFields(showIf) {
  const keys = [];
  if (showIf?.field) keys.push(showIf.field);
  if (Array.isArray(showIf?.conditions)) {
    showIf.conditions.forEach((c) => { if (c?.field) keys.push(c.field); });
  }
  return keys;
}

// A Show Condition may be a single leaf OR a `conditions` array combined by
// `logic` ("and" default | "or"), plus an optional leaf folded in with the same
// logic — mirrors the form-side evaluateShowIf so payload gating matches render.
export function showIfSatisfied(showIf, scope, root) {
  if (!showIf) return true;
  const conditions = Array.isArray(showIf.conditions) ? showIf.conditions.filter((c) => c?.field) : [];
  if (conditions.length) {
    const results = conditions.map((c) => showIfLeafSatisfied(c, scope, root));
    const orLogic = String(showIf.logic ?? 'and').toLowerCase() === 'or';
    let combined = orLogic ? results.some(Boolean) : results.every(Boolean);
    if (showIf.field) {
      const leaf = showIfLeafSatisfied(showIf, scope, root);
      combined = orLogic ? (combined || leaf) : (combined && leaf);
    }
    return combined;
  }
  return showIfLeafSatisfied(showIf, scope, root);
}

// fieldVisibleForPayload — transitive showIf: a field is storable only when its
// own condition passes AND the field its condition READS is itself storable
// (e.g. vmsCommission is gated on isVMSRequired, which is gated on
// contractType — on W2 all of them drop together even if stale values linger).
// `index` maps field key → field config across every group; `seen` guards
// against condition cycles.
function fieldVisibleForPayload(field, scope, root, index, seen = new Set()) {
  const gateFields = showIfConditionFields(field?.showIf);
  if (!gateFields.length) return true;
  if (seen.has(field.field)) return true; // cycle — fail open
  seen.add(field.field);
  if (!showIfSatisfied(field.showIf, scope, root)) return false;
  // Transitive: EACH gate field this condition reads must itself be storable.
  return gateFields.every((key) => {
    const gate = index?.get?.(key);
    return gate ? fieldVisibleForPayload(gate, scope, root, index, seen) : true;
  });
}

// payloadFieldIndex — field key → field config across all groups, for the
// transitive showIf walk above.
function payloadFieldIndex(groups = []) {
  const index = new Map();
  groups.forEach((g) => (g.fields ?? []).forEach((f) => {
    if (f?.field && !index.has(f.field)) index.set(f.field, f);
  }));
  return index;
}

// rowDefaultsFor — the seed object for a brand-new addRow row: every non-file
// field's configured defaultValue (e.g. VMS Commission 5.5, Rate Currency USD)
// keyed by its form key. Used for a group's initial empty rows AND every row
// the user adds, so admin defaults show inside repeatable groups too (top-level
// fields already get theirs via Form.Item initialValue).
export function rowDefaultsFor(group = {}, { editing = false } = {}) {
  const seed = {};
  (group.fields ?? []).forEach((f) => {
    // Same alias-tolerant upload test used everywhere else — a "document"-typed
    // upload must not be seeded with a defaultValue any more than a "file" one.
    if (!f.field || isUploadField(f)) return;
    if (editing && !f.defaultOnEdit) return;
    if (f.defaultValue === undefined || f.defaultValue === '') return;
    seed[f.field] = f.defaultValue;
  });
  return seed;
}

// ── field flattening from groups ──────────────────────────────────────────────

/** All leaf fields across groups, with their group context (for addRow arrays). */
export function flattenFields(groups = []) {
  const out = [];
  groups.forEach((g) => {
    (g.fields ?? []).forEach((f) => {
      out.push({ field: f, group: g });
    });
  });
  return out;
}

// UPLOAD_FIELD_TYPES — every field type that holds an uploaded file.
//
// "file" is the canonical type the form renderer uses, but admin config in the
// wild (and the seeded module defaults) also carries "document", "image",
// "upload" and "attachment" for the same intent. Those aliases used to be
// invisible to the collector, so their files silently never reached the shared
// `documents` collection — a module whose uploads simply never appeared in the
// registry, with no error anywhere. Matching on intent instead of on one exact
// spelling makes the central documents registry work for EVERY module by
// default, whichever synonym the config happens to use.
//
// This is safe for a field that is not really an upload: the collector only
// ever pushes actual File/Blob values (see push in collectFileParts), so a
// non-upload field simply contributes nothing.
export const UPLOAD_FIELD_TYPES = ['file', 'document', 'image', 'upload', 'attachment'];

// isUploadField is the SINGLE predicate every file-collection path uses, so the
// set of upload types can never drift between them again.
export function isUploadField(field) {
  return UPLOAD_FIELD_TYPES.includes(String(field?.type ?? '').trim().toLowerCase());
}

export function fileFields(groups = []) {
  return flattenFields(groups)
    .map(({ field }) => field)
    .filter(isUploadField);
}

/**
 * collectFileParts — gather the NEW files the user picked, ready to append to a
 * multipart request, so create AND edit upload through the same gateway endpoint.
 *
 * Returns [{ formKey, file }]. The part name is the field's `fileKey` if set
 * (e.g. jobs reads "file"), else the first segment of its path
 * ("passport.passportUploadName" → "passport"). addRow groups fan out across
 * every row. Only fresh uploads (antd `originFileObj`, or a raw File/Blob) are
 * included — existing/stored files (url only) are left alone. Config-driven; no
 * field-name or module-specific logic.
 *
 * opts.indexed — when true, files inside an addRow group are emitted under an
 * INDEXED part name ("documents[0]", "documents[1]", …) where the index is the
 * row's position. EditForm uses this because the candidates update handler reads
 * document files by indexed key and aligns each file to its metadata row by
 * index.
 *
 * opts.scope — 'all' (default) | 'flat' | 'addRow'. Lets a caller collect only
 * the non-repeatable (flat) file fields, or only the repeatable (addRow) ones.
 * AddForm uploads flat files in the create request, then attaches addRow files
 * (e.g. candidate documents) in a follow-up update — the create handler for
 * repeatable document files is unreliable, while the indexed update path is the
 * proven one. Config-driven (keyed on the group's addRow flag), no module names.
 */
// normalizeModuleKey trims + lower-cases a module key so a group's Target
// Collection can be compared against the form's own module case-insensitively.
//
// It deliberately does NOT singularize (strip a trailing "s"). The gateway is
// the source of truth for collection identity: it folds a moduleWrites entry
// back into the main record only when the target resolves to the SAME physical
// collection (foldSameCollectionModuleWrites → isSameCollectionTarget in
// moduleCrudController.go), and its NormalizeFormGroupModule does not blindly
// singularize either. A naive "trainers" → "trainer" fold here diverged from
// that and silently merged a genuinely-distinct collection (e.g. "trainers")
// into the primary record (e.g. module "trainer") — the exact multi-collection
// bug. Exact-match keeps the FE from over-folding; any real same-collection
// alias/plural case is folded server-side where the collection names are known.
function normalizeModuleKey(module) {
  return String(module ?? '').trim().toLowerCase();
}

// isSameModuleTarget — the group's Target Collection is the form's own module
// (exact, case-insensitive), i.e. the group writes to the MAIN record rather
// than a linked document. Plural/alias targets that still resolve to the main
// collection are folded by the gateway, not guessed here.
function isSameModuleTarget(group, primaryKey) {
  return Boolean(primaryKey) && normalizeModuleKey(group.moduleName) === primaryKey;
}

export function collectFileParts(values, groups = [], opts = {}) {
  const parts = [];
  const scope = opts.scope ?? 'all';
  const primaryKey = normalizeModuleKey(opts.module);
  const push = (formKey, node) => {
    if (node == null) return;
    const list = Array.isArray(node) ? node : [node];
    list.forEach((file) => {
      const raw = file?.originFileObj || file;
      if (typeof File !== 'undefined' && raw instanceof File) parts.push({ formKey, file: raw });
      else if (typeof Blob !== 'undefined' && raw instanceof Blob) parts.push({ formKey, file: raw });
    });
  };
  (groups ?? []).forEach((group) => {
    const files = (group.fields ?? []).filter(isUploadField);
    if (!files.length) return;
    // A moduleWrites group's files must be routed to ITS collection, not the
    // primary record — prefixed so the gateway's applyModuleWrites can tell
    // them apart (see splitModuleWriteFiles / filesForModuleWrite server-side).
    // A group targeting the form's own module writes to the main record, so
    // its files stay unprefixed (mirrors buildPayload's targetFor).
    const routed = group.moduleName && !isSameModuleTarget(group, primaryKey);
    const keyFor = (baseKey) => (routed ? `__mw__${group.moduleName}__${baseKey}` : baseKey);
    if (group.addRow) {
      if (scope === 'flat') return;
      const rows = values[group.name];
      if (!Array.isArray(rows)) return;
      files.forEach((field) => {
        const baseKey = field.fileKey ?? String(field.field).split('.')[0];
        rows.forEach((row, rowIdx) => {
          const formKey = keyFor(opts.indexed ? `${baseKey}[${rowIdx}]` : baseKey);
          push(formKey, readFormValue(row, field.field));
        });
      });
      return;
    }
    if (scope === 'addRow') return;
    files.forEach((field) => {
      const formKey = keyFor(field.fileKey ?? String(field.field).split('.')[0]);
      push(formKey, readFormValue(values, field.field));
    });
  });
  return parts;
}

// ── the two public builders ───────────────────────────────────────────────────

/**
 * buildPayload — config-driven payload object from antd form values.
 *
 * @param values  the antd form values object (may contain dot-notation nesting,
 *                Form.List arrays for addRow groups, and dayjs date objects)
 * @param groups  normalised form groups (with the per-field payload config)
 * @param opts    { base } optional base object to merge onto (edit keeps the
 *                untouched parts of the original record)
 * @returns       a plain payload object ready to JSON.stringify
 *
 * File-typed fields are skipped — the caller handles uploads separately.
 */
export function buildPayload(values, groups, opts = {}) {
  const payload = isPlainObject(opts.base) ? structuredClone(opts.base) : {};

  // Strip file fields out of any base so stale file arrays never ride along.
  fileFields(groups).forEach((f) => {
    if (f.payloadKey || f.field) {
      // best-effort removal at both the configured key and source key
      deleteDeep(payload, f.payloadKey || f.field);
      deleteDeep(payload, f.field);
    }
  });

  // moduleWrites — a group with `moduleName` set (Feature 1: Per-Group Target
  // Collection) is routed to a DIFFERENT collection than the default record,
  // so its fields build their OWN sub-payload instead of merging into
  // `payload`. Groups sharing the same moduleName merge into ONE entry — the
  // backend's applyModuleWrites (moduleCrudController.go) upserts them the
  // same way, into one secondary document per (primary record, moduleName).
  //
  // A target that IS the form's own module (opts.module) means "the main
  // record": routing it through moduleWrites would create a parentId-linked
  // TWIN document in the same collection, so it merges into `payload` instead.
  // The backend applies the same guard (foldSameCollectionModuleWrites).
  const primaryKey = normalizeModuleKey(opts.module);
  const fieldIndex = payloadFieldIndex(groups);
  const moduleWrites = new Map();
  const targetFor = (group) => {
    if (!group.moduleName || isSameModuleTarget(group, primaryKey)) return payload;
    if (!moduleWrites.has(group.moduleName)) moduleWrites.set(group.moduleName, {});
    return moduleWrites.get(group.moduleName);
  };

  (groups ?? []).forEach((group) => {
    const target = targetFor(group);
    const groupFields = group.fields ?? [];
    const groupToggleOn = group.toggleEnabled && group.toggleField && hasFormValue(values, group.toggleField)
      ? Boolean(readFormValue(values, group.toggleField))
      : false;
    if (group.toggleEnabled && group.toggleField && hasFormValue(values, group.toggleField)) {
      setDeep(target, group.toggleField, groupToggleOn);
      if (groupToggleOn && group.addRow) {
        setDeep(target, group.payloadKey || group.name, []);
        return;
      }
    }

    if (group.addRow) {
      // Repeatable group → ROW-oriented array of objects:
      //   [{ fieldA: v, fieldB: v }, { ... }]
      // matching a Go []struct (e.g. workExperience, educationDetails, documents).
      // The output key is the group's payloadKey (the struct's array key, e.g.
      // "workExperience") falling back to the group name.
      const rows = values[group.name];
      // Not present in this submit (e.g. EditForm doesn't prefill Form.List) →
      // leave whatever the base record already has untouched.
      if (!Array.isArray(rows)) return;
      const outKey = group.payloadKey || group.name;
      const rowObjects = rows
        .map((row) => buildRowObject(groupFields, row, values, fieldIndex))
        .filter((obj) => obj && Object.keys(obj).length > 0);
      setDeep(target, outKey, rowObjects);
      return;
    }

    groupFields.forEach((field) => writeField(field, values, target, values, fieldIndex));
  });

  if (moduleWrites.size > 0) {
    payload.moduleWrites = Array.from(moduleWrites, ([moduleName, data]) => ({ moduleName, data }));
  }

  return securePayload(payload, groups);
}

// writeField — the single, shared rule for turning ONE field's value (read from
// `scope`, which is the whole form values for a normal group or a single row
// object for an addRow row) into its contribution on `target`. Used by both
// buildPayload (top-level groups) and buildRowObject (addRow rows) so a field
// behaves identically no matter how deeply it is nested — a repeatable
// field-GROUP (subFields) or a repeatable scalar field (addRow) serialises to a
// nested array the same way at any level. Config-driven; no field-name logic.
function writeField(field, scope, target, root, fieldIndex) {
  // An admin-configured validation override (field.overrideCheckbox) writes its
  // own flag alongside the field, in whatever scope the field itself lives in
  // (flat payload or one addRow row). Recorded because "someone deliberately
  // lifted this limit" is an audit fact about the record, not a UI detail —
  // without it a value that breaks a documented rule looks like the rule failed.
  // `store: false` opts out for a purely transient override.
  const overrideCfg = overrideConfig(field);
  if (overrideCfg && overrideIsStored(field)) {
    const flagKey = overrideFieldKey(field);
    if (hasFormValue(scope, flagKey)) {
      setDeep(target, flagKey, Boolean(readFormValue(scope, flagKey)));
    }
  }

  // isUploadField, NOT `type === 'file'`. Admin config in the wild types an
  // upload as "document"/"image"/"upload"/"attachment" just as often, and those
  // aliases used to MISS this branch entirely: the antd fileList
  // ([{uid,name,stored,…}]) then fell through to the ordinary scalar path and
  // was written to the payload as the field's value, overwriting the stored file
  // container with UI junk — or, when the user had not touched the field and it
  // held an empty list, writing [] over it. That is the "an uploaded document
  // disappears when you edit the record" bug: the update wiped the container the
  // form never intended to change. Every upload type now takes the branch below,
  // whose contract is "carry the stored reference back, or write NOTHING" —
  // never null, never an empty array, in any scope (flat field, addRow row, or
  // indexed document row: buildRowObject routes through this same function).
  if (isUploadField(field)) {
    // A NEW upload rides the multipart request (collectFileParts) and the
    // downstream handler writes its stored shape — nothing to do here.
    // An ALREADY-STORED file has no File object to upload, so without carrying
    // its reference the record ends up with no document at all: that is why a
    // Quick Submit prefilled from a previous submission lost every document it
    // showed in the form. Write back the untouched original container, merged
    // so sibling scalars written from the same container (passport.number,
    // passport.expiry) survive regardless of field order.
    const carried = carriedFileValue(readFormValue(scope, field.field));
    if (carried !== undefined) {
      // At the top level the fileKey names the stored container ("passport",
      // "resume"); inside an addRow row it is the multipart part name for the
      // whole group, so only the row's own key applies there.
      const inRow = root !== undefined && root !== scope;
      const key = field.payloadKey || (inRow ? field.field : (field.fileKey || field.field));
      mergeDeepAt(target, key, carried);
    }
    return;
  }

  // showIf gating at PAYLOAD time: a field whose visibility condition fails is
  // not stored. antd preserves unmounted Form.Item values, so without this a
  // value typed before the condition flipped (e.g. VMS Commission after VMS
  // Required was unchecked) would silently ride along. Writing null (rather
  // than skipping) also clears the stale stored value on edit; inside an
  // addRow row the whole row array is rewritten anyway, so the key simply
  // drops out of the row object. Visibility is TRANSITIVE: a field whose gate
  // field is itself hidden drops too (fieldVisibleForPayload).
  if (showIfConditionFields(field.showIf).length && !fieldVisibleForPayload(field, scope, root, fieldIndex)) {
    // Top-level (scope === root): write null so an edit clears the stale
    // stored value. Row scope: the row array is rewritten wholesale, so simply
    // omitting the key removes it from the stored row.
    if ((root === undefined || root === scope) && hasFormValue(scope, field.field)) {
      setDeep(target, field.payloadKey || field.field, null);
    }
    return;
  }

  // Repeatable field-GROUP (subFields): each Form.List row is an object of the
  // sub-fields, so this field submits as an array of row objects —
  // [{question, answer}, …] — under its own payloadKey. Recurses so a nested
  // repeat group (e.g. inside an addRow row) is shaped element-by-element.
  if (Array.isArray(field.subFields) && field.subFields.length > 0) {
    if (!hasFormValue(scope, field.field)) return;
    const rows = readFormValue(scope, field.field);
    if (!Array.isArray(rows)) return;
    const rowObjects = rows
      .map((row) => buildRowObject(field.subFields, row, root, fieldIndex))
      .filter((obj) => obj && Object.keys(obj).length > 0);
    setDeep(target, field.payloadKey || field.field, rowObjects);
    return;
  }

  if (!hasFormValue(scope, field.field)) return; // not in this submit — leave base untouched

  // Repeatable SCALAR field (addRow, no subFields): each Form.List item is one
  // value, so normalise every element through the field's single-value shape
  // (dates → ISO, select → id) instead of relying on a blanket array coercion.
  // This makes a repeat field work INSIDE an addRow row (subjects[] per row) as
  // well as at the top level.
  if (truthy(field.addRow) && (!Array.isArray(field.subFields) || field.subFields.length === 0)) {
    const raw = readFormValue(scope, field.field);
    const arr = Array.isArray(raw) ? raw : isEmpty(raw) ? [] : [raw];
    const single = singleFieldOf(field);
    const out = arr
      .map((el) => shapeFieldValue(single, el))
      .filter((r) => r.kind !== 'skip')
      .map((r) => r.out);
    setDeep(target, field.payloadKey || field.field, out);
    return;
  }

  const raw = readFormValue(scope, field.field);
  const result = shapeFieldValue(field, raw);
  if (result.kind === 'skip') return;
  if (result.kind === 'spread' && isPlainObject(result.out)) {
    mergeDeep(target, result.out);
    return;
  }
  setDeep(target, field.payloadKey || field.field, result.out);
}

// buildRowObject — shape one addRow row into a plain object keyed by each
// field's payloadKey/field (relative to the row). File fields are skipped (they
// upload separately and their metadata is merged server-side).
function buildRowObject(groupFields, row, root, fieldIndex) {
  const obj = {};
  groupFields.forEach((field) => writeField(field, row, obj, root ?? row, fieldIndex));
  return obj;
}

function deleteDeep(obj, path) {
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (!isPlainObject(cur[parts[i]])) return;
    cur = cur[parts[i]];
  }
  delete cur[parts[parts.length - 1]];
}

/**
 * buildInitialValue — reverse direction, for EditForm prefill.
 * Given a field config and the stored value (which may be a scalar, an
 * { id, value } reference, an { id, name } object, or an array of those),
 * returns the value the input control expects.
 *
 *   - date/time fields → dayjs
 *   - select/radio/checkbox → the id/value (single) or array of ids (multi)
 *   - everything else → the scalar
 *
 * This replaces convertInitialValue's hardcoded field-name handling.
 */
// Controls that render exactly one primitive. Deliberately excludes select /
// lookup / reference / file types, whose values are legitimately objects.
const SCALAR_CONTROL_TYPES = new Set([
  'text', 'textarea', 'email', 'phone', 'tel', 'number', 'date', 'time', 'datetime', 'password',
]);

export function buildInitialValue(field, stored) {
  const configuredEmpty = (value) => {
    const emptyValues = Array.isArray(field.emptyValues) ? field.emptyValues : [];
    return emptyValues.some((v) => String(v) === String(value));
  };
  const fallbackEditDefault = () =>
    field.defaultOnEdit && field.defaultValue !== undefined && field.defaultValue !== ''
      ? field.defaultValue
      : undefined;

  if (stored === undefined || stored === null) {
    return fallbackEditDefault() ?? stored;
  }
  if (configuredEmpty(stored)) {
    return fallbackEditDefault();
  }
  if (isEmpty(stored) && field.defaultOnEdit && field.defaultValue !== undefined && field.defaultValue !== '') {
    return field.defaultValue;
  }

  // A text/number/date control can only show a scalar. When the stored value is
  // a whole sub-document — a field whose key names its own container, e.g.
  // "address" storing {addressLine, city, state} while its payloadKey is
  // "address.addressLine" — reduce it to the payloadKey's leaf. Without this the
  // input renders "[object Object]" and saving writes that string over the real
  // address. Falls through to undefined (blank control) when no leaf matches:
  // never destructive, always recoverable.
  if (SCALAR_CONTROL_TYPES.has(field.type) && isPlainObject(stored)) {
    const leaf = String(field.payloadKey || field.field || '').split('.').pop();
    const reduced = leaf ? stored[leaf] : undefined;
    if (reduced === undefined || isPlainObject(reduced)) return undefined;
    stored = reduced; // eslint-disable-line no-param-reassign
  }

  if (field.type === 'date' || field.type === 'time') {
    if (!stored || stored === '0001-01-01T00:00:00Z') return null;
    const p = dayjs(stored);
    return p.isValid() ? p : null;
  }

  // Snap onto the field's configured option before the control sees it. A
  // select/radio only selects on an exact value match, so an incoming "onsite"
  // or "ONSITE" would leave an "On-Site" radio blank even though the value
  // arrived. Fields without options, and values that match nothing, pass
  // through untouched — this can correct a value but never discard one.
  stored = snapToOption(field, stored);

  const pickId = (item) => {
    if (isPlainObject(item)) {
      return (
        (field.valueKey && item[field.valueKey]) ??
        item.id ??
        item._id ??
        item.value ??
        item.userId ??
        item.recruiterId
      );
    }
    return item;
  };

  // Radio groups are ALWAYS single-select in the UI, even when the payload
  // dataType is "array" (e.g. jobRemoteStatus → []string). Prefill the scalar
  // so the selected radio shows; the payload engine re-wraps it to an array on
  // save via coerceDataType. Without this, a radio gets an array value and
  // renders with nothing selected.
  const isMulti = field.type === 'radio' ? false : effectiveDataType(field) === 'array';

  // A stored value that only differs from a static option by case (e.g. a
  // legacy "active" vs the configured option value "Active") would otherwise
  // match nothing and render as an unselected/empty control.
  const matchOptionCase = (v) => {
    if (v === undefined || v === null || v === '' || !Array.isArray(field.options)) return v;
    const match = field.options.find((option) => String(option?.value ?? '').toLowerCase() === String(v).toLowerCase());
    return match ? match.value : v;
  };

  if (field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') {
    if (isMulti) {
      const arr = Array.isArray(stored) ? stored : [stored];
      return arr.map(pickId).map(matchOptionCase).filter((v) => v !== undefined && v !== null && v !== '' && !configuredEmpty(v));
    }
    const selected = matchOptionCase(Array.isArray(stored) ? pickId(stored[0]) : pickId(stored));
    return configuredEmpty(selected) ? fallbackEditDefault() : selected;
  }

  // checkbox group with a transformRule map (e.g. "high" → checked) is handled
  // by the caller via the same map; here we just pass the scalar through.
  return stored;
}

// singleFieldOf strips a repeatable (addRow) field down to its per-item shape:
// each Form.List item holds ONE value, so multi/array coercion must NOT apply
// when reading or rendering a single item. Preserves everything else (type,
// options, datasource, validations) so the item control still behaves like the
// field otherwise would.
export function singleFieldOf(field = {}) {
  return { ...field, addRow: false, multiSelect: false, mode: undefined, dataType: undefined };
}

// buildRepeatFieldInitial — the Form.List initialValue for a field-level repeat
// (field.addRow). Unlike a repeatable GROUP (rows are objects), each item here
// is a single scalar, so a stored array maps element-by-element through
// buildInitialValue as if the field were single (see singleFieldOf). The result
// is padded to minRows; when there is no stored value at all, initialRows
// (falling back to minRows) empty inputs are shown so the user sees a starting
// control instead of only a "+" button. Config keys mirror the group ones:
// field.minRows / field.initialRows.
export function buildRepeatFieldInitial(field, stored) {
  const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
  const initialRows = field.initialRows !== undefined && field.initialRows !== ''
    ? Math.max(minRows, Math.max(0, Number(field.initialRows) || 0))
    : minRows;

  // Repeatable field-GROUP (subFields): each stored element is a row OBJECT, so
  // map every sub-field through buildInitialValue into a per-row object (mirrors
  // getAddRowInitialValue's group-addRow row mapping). Empty rows fall back to {}
  // so the sub-field controls still render.
  if (Array.isArray(field.subFields) && field.subFields.length > 0) {
    let rows = [];
    if (Array.isArray(stored)) {
      rows = stored.map((row) => {
        const out = {};
        field.subFields.forEach((sub) => {
          if (!sub.field || isUploadField(sub)) return;
          const v = getDeep(row ?? {}, sub.field) ?? (row ?? {})[sub.field];
          if (v !== undefined && v !== null) out[sub.field] = buildInitialValue(sub, v);
        });
        return out;
      });
    }
    const target = rows.length > 0 ? minRows : Math.max(minRows, initialRows);
    while (rows.length < target) rows.push({});
    return rows;
  }

  const singleField = singleFieldOf(field);
  let items = [];
  if (Array.isArray(stored)) {
    items = stored
      .map((v) => buildInitialValue(singleField, v))
      .filter((v) => v !== undefined && v !== null && v !== '');
  } else if (stored !== undefined && stored !== null && stored !== '') {
    const v = buildInitialValue(singleField, stored);
    if (v !== undefined && v !== null && v !== '') items = [v];
  }

  const target = items.length > 0 ? minRows : Math.max(minRows, initialRows);
  while (items.length < target) items.push(undefined);
  return items;
}

export default {
  buildPayload,
  buildInitialValue,
  showIfSatisfied,
  rowDefaultsFor,
  buildRepeatFieldInitial,
  singleFieldOf,
  shapeFieldValue,
  usesLabeledSelectValue,
  toSelectControlValue,
  coerceDataType,
  getDeep,
  setDeep,
  flattenFields,
  fileFields,
};
